/**
 * Gamification Store - Zustand
 * Hanterar poängsystem för motivation och engagemang
 * 
 * Två typer av poäng:
 * - Kunskapspoäng: Tjänas genom att läsa artiklar, slutföra quiz, lära sig nya färdigheter
 * - Resurspoäng: Tjänas genom att bocka av checklistor, uppdatera förråd, utföra underhåll
 * 
 * Varje område har sina egna poäng för båda typerna.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Beredskapområden
export type PrepArea = 
  | 'water' 
  | 'food' 
  | 'energy' 
  | 'health' 
  | 'information' 
  | 'shelter' 
  | 'transport'
  | 'communication'
  | 'safety'
  | 'shopping'
  | 'gardening'
  | 'other';

export type PointType = 'knowledge' | 'resource';

export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string; // Emoji eller ikon-namn
  rarity: BadgeRarity;
  unlockedAt: string | null;
  requirement: {
    type: 'knowledge' | 'resource' | 'total' | 'streak' | 'special';
    area?: PrepArea;
    threshold: number;
  };
}

export interface PointActivity {
  id: string;
  type: PointType;
  area: PrepArea;
  points: number;
  reason: string;
  timestamp: string;
}

export interface AreaPoints {
  knowledge: number;
  resource: number;
}

export interface LevelInfo {
  level: number;
  title: string;
  minPoints: number;
  maxPoints: number;
  progress: number; // 0-100
}

interface GamificationState {
  // Poäng per område
  areaPoints: Record<PrepArea, AreaPoints>;
  
  // Totala poäng
  totalKnowledgePoints: number;
  totalResourcePoints: number;
  
  // Aktivitetshistorik (senaste 50)
  recentActivities: PointActivity[];
  
  // Badges/utmärkelser
  badges: Badge[];
  
  // Streaks
  dailyStreak: number;
  lastActivityDate: string | null;
  longestStreak: number;
  
  // Actions
  addPoints: (type: PointType, area: PrepArea, points: number, reason: string) => void;
  checkAndAwardBadges: () => string[]; // Returnerar nya badge-IDs
  updateStreak: () => void;
  
  // Getters
  getTotalPoints: () => number;
  getAreaTotal: (area: PrepArea) => number;
  getLevel: () => LevelInfo;
  getUnlockedBadges: () => Badge[];
  getNextBadges: () => Badge[];
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// Nivåer baserade på totala poäng
const levels: { level: number; title: string; minPoints: number }[] = [
  { level: 1, title: 'Nybörjare', minPoints: 0 },
  { level: 2, title: 'Lärling', minPoints: 100 },
  { level: 3, title: 'Förberedare', minPoints: 250 },
  { level: 4, title: 'Beredskapare', minPoints: 500 },
  { level: 5, title: 'Veteran', minPoints: 1000 },
  { level: 6, title: 'Expert', minPoints: 2000 },
  { level: 7, title: 'Mästare', minPoints: 3500 },
  { level: 8, title: 'Guru', minPoints: 5000 },
  { level: 9, title: 'Legend', minPoints: 7500 },
  { level: 10, title: 'Beredskapshjälte', minPoints: 10000 },
];

// Fördefinierade badges
const defaultBadges: Badge[] = [
  // Startbadges
  {
    id: 'first-steps',
    name: 'Första stegen',
    description: 'Tjänade dina första poäng',
    icon: '👣',
    rarity: 'common',
    unlockedAt: null,
    requirement: { type: 'total', threshold: 1 },
  },
  {
    id: 'water-apprentice',
    name: 'Vattenlärling',
    description: 'Samlade 50 poäng inom vatten',
    icon: '💧',
    rarity: 'common',
    unlockedAt: null,
    requirement: { type: 'resource', area: 'water', threshold: 50 },
  },
  {
    id: 'knowledge-seeker',
    name: 'Kunskapssökare',
    description: 'Samlade 100 kunskapspoäng',
    icon: '📚',
    rarity: 'common',
    unlockedAt: null,
    requirement: { type: 'knowledge', threshold: 100 },
  },
  // Rare badges
  {
    id: 'water-expert',
    name: 'Vattenexpert',
    description: 'Samlade 200 poäng inom vatten',
    icon: '🌊',
    rarity: 'rare',
    unlockedAt: null,
    requirement: { type: 'resource', area: 'water', threshold: 200 },
  },
  {
    id: 'food-master',
    name: 'Matmästare',
    description: 'Samlade 200 poäng inom mat',
    icon: '🍳',
    rarity: 'rare',
    unlockedAt: null,
    requirement: { type: 'resource', area: 'food', threshold: 200 },
  },
  {
    id: 'energy-guru',
    name: 'Energiguru',
    description: 'Samlade 200 poäng inom energi',
    icon: '⚡',
    rarity: 'rare',
    unlockedAt: null,
    requirement: { type: 'resource', area: 'energy', threshold: 200 },
  },
  {
    id: 'health-hero',
    name: 'Hälsohjälte',
    description: 'Samlade 200 poäng inom hälsa',
    icon: '❤️‍🩹',
    rarity: 'rare',
    unlockedAt: null,
    requirement: { type: 'resource', area: 'health', threshold: 200 },
  },
  {
    id: 'week-streak',
    name: 'Veckokämpe',
    description: 'Aktiv 7 dagar i rad',
    icon: '🔥',
    rarity: 'rare',
    unlockedAt: null,
    requirement: { type: 'streak', threshold: 7 },
  },
  // Epic badges
  {
    id: 'resource-collector',
    name: 'Resurssamlare',
    description: 'Samlade 500 resurspoäng totalt',
    icon: '📦',
    rarity: 'epic',
    unlockedAt: null,
    requirement: { type: 'resource', threshold: 500 },
  },
  {
    id: 'knowledge-master',
    name: 'Kunskapsmästare',
    description: 'Samlade 500 kunskapspoäng totalt',
    icon: '🎓',
    rarity: 'epic',
    unlockedAt: null,
    requirement: { type: 'knowledge', threshold: 500 },
  },
  {
    id: 'month-streak',
    name: 'Månadsmästare',
    description: 'Aktiv 30 dagar i rad',
    icon: '🏆',
    rarity: 'epic',
    unlockedAt: null,
    requirement: { type: 'streak', threshold: 30 },
  },
  {
    id: 'all-rounder',
    name: 'Allroundare',
    description: 'Minst 100 poäng i alla områden',
    icon: '🌟',
    rarity: 'epic',
    unlockedAt: null,
    requirement: { type: 'special', threshold: 100 },
  },
  // Legendary badges
  {
    id: 'preparedness-legend',
    name: 'Beredskapslegend',
    description: 'Samlade 2000 totala poäng',
    icon: '👑',
    rarity: 'legendary',
    unlockedAt: null,
    requirement: { type: 'total', threshold: 2000 },
  },
  {
    id: 'year-streak',
    name: 'Årskämpe',
    description: 'Aktiv 365 dagar i rad',
    icon: '💎',
    rarity: 'legendary',
    unlockedAt: null,
    requirement: { type: 'streak', threshold: 365 },
  },
];

// Initial poäng per område
const initialAreaPoints: Record<PrepArea, AreaPoints> = {
  water: { knowledge: 0, resource: 0 },
  food: { knowledge: 0, resource: 0 },
  energy: { knowledge: 0, resource: 0 },
  health: { knowledge: 0, resource: 0 },
  information: { knowledge: 0, resource: 0 },
  shelter: { knowledge: 0, resource: 0 },
  transport: { knowledge: 0, resource: 0 },
  communication: { knowledge: 0, resource: 0 },
  safety: { knowledge: 0, resource: 0 },
  shopping: { knowledge: 0, resource: 0 },
  gardening: { knowledge: 0, resource: 0 },
  other: { knowledge: 0, resource: 0 },
};

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

export const useGamificationStore = create<GamificationState>()(
  persist(
    (set, get) => ({
      areaPoints: { ...initialAreaPoints },
      totalKnowledgePoints: 0,
      totalResourcePoints: 0,
      recentActivities: [],
      badges: [...defaultBadges],
      dailyStreak: 0,
      lastActivityDate: null,
      longestStreak: 0,
      
      addPoints: (type, area, points, reason) => {
        const activity: PointActivity = {
          id: generateId(),
          type,
          area,
          points,
          reason,
          timestamp: new Date().toISOString(),
        };
        
        set((state) => {
          const newAreaPoints = { ...state.areaPoints };
          
          // Ensure area exists before updating
          if (newAreaPoints[area]) {
            newAreaPoints[area] = {
              ...newAreaPoints[area],
              [type]: newAreaPoints[area][type] + points,
            };
          }
          
          const newTotalKnowledge = type === 'knowledge' 
            ? state.totalKnowledgePoints + points 
            : state.totalKnowledgePoints;
          const newTotalResource = type === 'resource'
            ? state.totalResourcePoints + points
            : state.totalResourcePoints;
          
          // Behåll senaste 50 aktiviteter
          const newActivities = [activity, ...state.recentActivities].slice(0, 50);
          
          return {
            areaPoints: newAreaPoints,
            totalKnowledgePoints: newTotalKnowledge,
            totalResourcePoints: newTotalResource,
            recentActivities: newActivities,
          };
        });
        
        // Uppdatera streak och kolla badges
        get().updateStreak();
        get().checkAndAwardBadges();
      },
      
      checkAndAwardBadges: () => {
        const state = get();
        const newBadgeIds: string[] = [];
        const now = new Date().toISOString();
        
        const updatedBadges = state.badges.map((badge) => {
          // Redan upplåst
          if (badge.unlockedAt) return badge;
          
          let earned = false;
          const { type, area, threshold } = badge.requirement;
          
          switch (type) {
            case 'total':
              earned = (state.totalKnowledgePoints + state.totalResourcePoints) >= threshold;
              break;
            case 'knowledge':
              if (area) {
                earned = state.areaPoints[area].knowledge >= threshold;
              } else {
                earned = state.totalKnowledgePoints >= threshold;
              }
              break;
            case 'resource':
              if (area) {
                earned = state.areaPoints[area].resource >= threshold;
              } else {
                earned = state.totalResourcePoints >= threshold;
              }
              break;
            case 'streak':
              earned = state.dailyStreak >= threshold || state.longestStreak >= threshold;
              break;
            case 'special':
              // All-rounder: minst threshold poäng i alla områden
              if (badge.id === 'all-rounder') {
                const areas = Object.values(state.areaPoints);
                earned = areas.every(ap => (ap.knowledge + ap.resource) >= threshold);
              }
              break;
          }
          
          if (earned) {
            newBadgeIds.push(badge.id);
            return { ...badge, unlockedAt: now };
          }
          
          return badge;
        });
        
        if (newBadgeIds.length > 0) {
          set({ badges: updatedBadges });
        }
        
        return newBadgeIds;
      },
      
      updateStreak: () => {
        const today = new Date().toISOString().split('T')[0];
        const { lastActivityDate, dailyStreak, longestStreak } = get();
        
        if (lastActivityDate === today) {
          // Redan loggat idag
          return;
        }
        
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        
        let newStreak = 1;
        if (lastActivityDate === yesterdayStr) {
          // Fortsätter streak
          newStreak = dailyStreak + 1;
        }
        
        set({
          dailyStreak: newStreak,
          lastActivityDate: today,
          longestStreak: Math.max(longestStreak, newStreak),
        });
      },
      
      getTotalPoints: () => {
        const { totalKnowledgePoints, totalResourcePoints } = get();
        return totalKnowledgePoints + totalResourcePoints;
      },
      
      getAreaTotal: (area) => {
        const { areaPoints } = get();
        return areaPoints[area].knowledge + areaPoints[area].resource;
      },
      
      getLevel: () => {
        const totalPoints = get().getTotalPoints();
        
        // Hitta nuvarande nivå
        let currentLevel = levels[0];
        let nextLevel = levels[1];
        
        for (let i = levels.length - 1; i >= 0; i--) {
          if (totalPoints >= levels[i].minPoints) {
            currentLevel = levels[i];
            nextLevel = levels[i + 1] || levels[i];
            break;
          }
        }
        
        // Beräkna progress till nästa nivå
        const pointsInLevel = totalPoints - currentLevel.minPoints;
        const pointsNeeded = nextLevel.minPoints - currentLevel.minPoints;
        const progress = pointsNeeded > 0 
          ? Math.min(100, Math.round((pointsInLevel / pointsNeeded) * 100))
          : 100;
        
        return {
          level: currentLevel.level,
          title: currentLevel.title,
          minPoints: currentLevel.minPoints,
          maxPoints: nextLevel.minPoints,
          progress,
        };
      },
      
      getUnlockedBadges: () => {
        return get().badges.filter(b => b.unlockedAt !== null);
      },
      
      getNextBadges: () => {
        return get().badges
          .filter(b => b.unlockedAt === null)
          .slice(0, 3);
      },
      
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'beredskapshubben-gamification',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        areaPoints: state.areaPoints,
        totalKnowledgePoints: state.totalKnowledgePoints,
        totalResourcePoints: state.totalResourcePoints,
        recentActivities: state.recentActivities,
        badges: state.badges,
        dailyStreak: state.dailyStreak,
        lastActivityDate: state.lastActivityDate,
        longestStreak: state.longestStreak,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<GamificationState> | undefined;
        if (!persisted) return currentState;
        
        // Merge areaPoints - ensure all areas exist (for migration when new areas are added)
        const mergedAreaPoints = { ...initialAreaPoints };
        if (persisted.areaPoints) {
          Object.keys(persisted.areaPoints).forEach((area) => {
            mergedAreaPoints[area as PrepArea] = persisted.areaPoints![area as PrepArea];
          });
        }
        
        return {
          ...currentState,
          ...persisted,
          areaPoints: mergedAreaPoints,
        };
      },
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Poängvärden för olika aktiviteter
export const POINT_VALUES = {
  // Kunskap
  READ_ARTICLE: 10,
  COMPLETE_QUIZ: 25,
  WATCH_VIDEO: 15,
  COMPLETE_COURSE: 50,
  
  // Resurser
  CHECKLIST_ITEM: 5,
  COMPLETE_CHECKLIST: 20,
  ADD_INVENTORY: 5,
  UPDATE_WATER: 10,
  COMPLETE_AREA: 100,
  COMPLETE_TASK: 15, // Årshjul - underhållsuppgift
  
  // Special
  DAILY_LOGIN: 2,
  COMPLETE_TEST: 30,
  FIRST_TIME_AREA: 15,
};

// Helper hooks
export const useTotalPoints = () => useGamificationStore((state) => state.getTotalPoints());
export const useLevel = () => useGamificationStore((state) => state.getLevel());
export const useStreak = () => useGamificationStore((state) => ({
  current: state.dailyStreak,
  longest: state.longestStreak,
}));
